import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import type { NewsletterSignUp as NewsletterSignUpSchema } from '@amplience/content-types/typings/c-footer-newsletter-sign-up';
import type { RichTextEditor } from '@amplience/content-types/typings/p-partials';
import { yupResolver } from '@hookform/resolvers/yup';
import { NewsletterSchema } from '@shared/validation/src/storefront/newsletter';
import { useMountRecaptchaScript } from '../../../hooks/useMountRecaptchaScript';
import { useNewsletter } from '../../../hooks/useNewsletter';
import { useResizeScrollSize } from '../../../hooks/useResizeScrollSize';
import { Tick } from '../../../icons';
import { TypographyStyles, TypographyStylesType, colours } from '../../../stylings';
import type { MarketingCategory } from '../../../types/marketingPreferences';
import { getLocalizedValue } from '../../../utils/transformers';
import { LanguageSelection } from '../languageSelection/LanguageSelection';
import { SuccessMessage } from '../successMessage/SuccessMessage';
import { TermsAndPolicy } from '../successMessage/SuccessMessage.style';
import {
  Button,
  Details,
  ErrorStyled as Error,
  FormContainer,
  FullWidthContainer,
  Heading,
  Input,
  InputAndButton,
  InputContainer,
  NewsletterSignUpContainer,
  Selection,
  SelectionList,
  SelectionListContainer,
  SubHeading,
} from './NewsletterSignUp.style';

export type TermsAndPolicyType = {
  richText: RichTextEditor;
};

export type NewsletterSignUpProps = {
  termsAndPolicy?: TermsAndPolicyType;
} & NewsletterSignUpSchema;

export const getTranslation = (value: string) => {
  if (value === 'kids') return 'footer.newsletter.kids';
  if (value === 'womens') return 'footer.newsletter.womens';
  if (value === 'mens') return 'footer.newsletter.mens';
  if (value === 'outlet') return 'footer.newsletter.outlet';
  if (value === 'originals') return 'footer.newsletter.originals';
  return '';
};

export function NewsletterSignUp({
  newsHeading: heading,
  bgColor,
  newsSubHeading: subHeading,
  termsAndPolicy,
  showSelections,
}: NewsletterSignUpProps) {
  const { t } = useTranslation('lib-global-common');

  const {
    checkboxError,
    newsletterData,
    onLanguageChange,
    onMarketingChoicesChange,
    onSubmit,
    recaptchaError,
    marketingPreferences,
    marketingChoices,
    languageSelectionError,
    loading,
  } = useNewsletter();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(NewsletterSchema),
    defaultValues: {
      email: '',
    },
  });
  const hasMultipleLanguages = (marketingPreferences && marketingPreferences.languages.length > 1) || false;
  const { elementRef, scrollWidth } = useResizeScrollSize({});
  const { componentRef } = useMountRecaptchaScript();
  return (
    <FullWidthContainer ref={componentRef} $bgColor={bgColor} data-testid="newsletterSection">
      <NewsletterSignUpContainer>
        <Details>
          {heading?.text && (
            <Heading
              {...heading}
              text={getLocalizedValue(heading?.text)}
              defaultTextSize={TypographyStylesType.HEADINGS_H3}
              defaultTextColor={colours.BLACK}
            />
          )}
          {subHeading?.text && (
            <SubHeading
              {...subHeading}
              text={getLocalizedValue(subHeading?.text)}
              defaultTextSize={TypographyStylesType.BODY_TINY_REGULAR}
              defaultTextColor={colours.BLACK}
            />
          )}
        </Details>
        {!newsletterData ? (
          <FormContainer onSubmit={handleSubmit(onSubmit)} noValidate>
            <div>
              {!!showSelections && (
                <SelectionListContainer>
                  <SelectionList
                    $hasMultipleLanguages={hasMultipleLanguages}
                    label={t('footer.newsletter.radio.category.selection')}
                  >
                    {marketingPreferences &&
                      marketingPreferences.marketingPreferences &&
                      Object.entries(marketingPreferences?.marketingPreferences as MarketingCategory).map(
                        ([key, item]) => (
                          <Selection key={`${item.key}-${key}`}>
                            <input
                              checked={marketingChoices[+key]?.optIn}
                              aria-invalid={checkboxError}
                              aria-describedby={checkboxError ? 'newsletter-checkbox-error' : undefined}
                              aria-required="true"
                              type="checkbox"
                              id={`newsletter-${item.key}`}
                              value={item.key}
                              onChange={onMarketingChoicesChange}
                              name={key}
                              data-testid="newsletterCheckbox"
                            />
                            <Tick viewBox="-6 0 40 25" />
                            <label htmlFor={`newsletter-${item.key}`}>{t(getTranslation(item.key))}</label>
                          </Selection>
                        ),
                      )}
                  </SelectionList>
                  {hasMultipleLanguages ? (
                    <LanguageSelection
                      languages={marketingPreferences?.languages || []}
                      onChange={onLanguageChange}
                      languageSelectionError={languageSelectionError}
                      uniqueKey="newsletter"
                    />
                  ) : null}
                </SelectionListContainer>
              )}
            </div>
            <InputContainer ref={elementRef}>
              <InputAndButton $scrollWidth={scrollWidth}>
                <div data-cs-mask>
                  <label className="visually-hidden" htmlFor="email">
                    {t('footer.newsletter.placeholder')}
                  </label>
                  <Input
                    type="email"
                    maxLength={60}
                    aria-label={t('footer.newsletter.placeholder')}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email?.message ? 'newsletter-email-error' : undefined}
                    aria-required="true"
                    placeholder={t('footer.newsletter.placeholder')}
                    id="email"
                    {...register('email')}
                    autoComplete="email"
                    required
                    data-testid="newsletterInput"
                  />
                  {errors.email?.message ? <Error id="newsletter-email-error">{t(errors.email.message)}</Error> : null}
                  {checkboxError && (
                    <Error id="newsletter-checkbox-error">{t('footer.newsletter.validation.checkbox')}</Error>
                  )}
                  {languageSelectionError && (
                    <Error id="newsletter-language-error">{t('footer.newsletter.validation.language')}</Error>
                  )}
                  {recaptchaError === 'BadDataError' && (
                    <Error id="recaptcha-error">{t('recaptcha.error.message')}</Error>
                  )}
                </div>
                <Button
                  $scrollWidth={scrollWidth}
                  disabled={loading}
                  type="submit"
                  data-testid="newsletterSubmitButton"
                >
                  {t('footer.newsletter.button')}
                </Button>
              </InputAndButton>
              <TermsAndPolicy
                text={getLocalizedValue(termsAndPolicy?.richText)}
                fontSize={TypographyStyles.Body.Tiny.Regular}
              />
            </InputContainer>
          </FormContainer>
        ) : (
          <SuccessMessage richText={termsAndPolicy?.richText} />
        )}
      </NewsletterSignUpContainer>
    </FullWidthContainer>
  );
}
